import { useOutletContext, useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import {
  ContractsEmployeesContractVersionFragment,
  FixFlowPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { FixFlowProvider } from 'modules/contracts/components/FixFlow/FixFlowProvider'
import FixFlowContent from 'modules/contracts/components/FixFlow/_FixFlowContent'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const FixFlowPage = () => {
  const { contractVersionId } = useParams()
  const { company } = useCurrentSession()
  const { data, loading } = useQuery(FixFlowPageDocument, {
    variables: {
      contractVersionId: String(contractVersionId),
      companyId: company.id,
    },
    skip: !contractVersionId,
  })

  const contractVersion = data?.contracts.contractVersionsConnection.nodes[0]
  const canEdit = contractVersion?._policies?.edit.can ?? false
  const hasLegacyJobCatalog =
    data?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'legacy_job_catalog'
    )?.enabled ?? false
  const jobCatalogTreeFeatureEnabled = !hasLegacyJobCatalog
  const jobCatalogTrees = data?.jobCatalog?.treesConnection?.nodes
  const legalEntityId = contractVersion?.employee?.legalEntity?.id ?? '0'
  const hasDevTimeTrackingWorkableDaysDefinition =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_time_tracking_workable_days_definition'
    )?.enabled ?? false
  const isVersionDataEnabled =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_contracts_data_as_json'
    )?.enabled ?? false
  const hasTimeTrackingAnnualisationDailyValue =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.time_tracking_annualisation_daily_value
    )?.available ?? false
  const hasPayrollBundle =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.payroll_management
    )?.available ?? false
  const jobCatalogContextlessPolicies = data?.jobCatalog?._policies
  const customFieldsContextlessPolicies = data?.customFields._policies

  const { contractVersions } = useOutletContext<{
    contractVersions: ContractsEmployeesContractVersionFragment[]
  }>()

  return (
    <FixFlowProvider
      contractVersionId={String(contractVersionId)}
      legalEntityId={legalEntityId}
      isVersionDataEnabled={isVersionDataEnabled}
    >
      <ResponsivePage.Body
        variant="fullscreen"
        loading={loading}
        error={!contractVersion}
        authorized={canEdit}
      >
        {!!contractVersion && (
          <FixFlowContent
            contractVersion={contractVersion}
            contractVersions={contractVersions}
            jobCatalogTrees={jobCatalogTrees}
            jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
            hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
            hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
            hasPayrollBundle={hasPayrollBundle}
            jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
            customFieldsContextlessPolicies={customFieldsContextlessPolicies}
          />
        )}
      </ResponsivePage.Body>
    </FixFlowProvider>
  )
}

export default FixFlowPage
